import { useEffect, useState } from "react";
import client from "../lib/api";
import { useMarket } from "../components/Layout";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { fmtPct } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const marketColor = (m) => m === "ASX" ? "#5eead4" : m === "NYSE" ? "#7db3ff" : "#b593ff";

const THRESHOLDS = [
  { v: "0.5", label: "≥ 50%" },
  { v: "0.6", label: "≥ 60%" },
  { v: "0.7", label: "≥ 70%" },
  { v: "0.8", label: "≥ 80%" },
];

export default function Backtest() {
  const market = useMarket();
  const [data, setData] = useState({ points: [], kpis: {} });
  const [horizon, setHorizon] = useState("r30");
  const [threshold, setThreshold] = useState(localStorage.getItem("ie_backtest_threshold") || "0.7");
  const [loading, setLoading] = useState(true);

  useEffect(() => { localStorage.setItem("ie_backtest_threshold", threshold); }, [threshold]);

  useEffect(() => {
    setLoading(true);
    client.get("/backtest", { params: { market, min_score: parseFloat(threshold) } }).then((r) => {
      setData(r.data); setLoading(false);
    });
  }, [market, threshold]);

  const kpis = data.kpis || {};
  const bench = kpis.benchmarks || {};
  const points = (data.points || []).filter(p => p[horizon] != null).map((p) => ({
    ...p, x: p.score * 100, y: p[horizon], fill: marketColor(p.market),
  }));

  return (
    <div data-testid="backtest-page">
      <div className="grid grid-cols-5 gap-4 mb-7">
        <Kpi label="HIT RATE vs BENCH" value={kpis.hit_rate != null ? `${kpis.hit_rate}%` : "—"} grad testKey="hit" />
        <Kpi label="MEAN T+7" value={fmtPct(kpis.mean_r7)} color={kpis.mean_r7 >= 0 ? "var(--score-high)" : "var(--pink)"} testKey="r7" />
        <Kpi label="MEAN T+30" value={fmtPct(kpis.mean_r30)} color={kpis.mean_r30 >= 0 ? "var(--score-high)" : "var(--pink)"} testKey="r30" />
        <Kpi label="MEAN T+90" value={fmtPct(kpis.mean_r90)} color={kpis.mean_r90 >= 0 ? "var(--score-high)" : "var(--pink)"} testKey="r90" />
        <Kpi label="ALPHA vs BENCH T+30" value={fmtPct(kpis.mean_alpha_30)} color={kpis.mean_alpha_30 >= 0 ? "var(--score-high)" : "var(--pink)"} testKey="alpha" />
      </div>

      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 className="text-[18px] font-semibold tracking-tight">Score vs Return</h2>
            <span className="mono text-[12px] text-[color:var(--text-tertiary)]">n={kpis.n ?? 0} · valid={kpis.n_valid ?? 0}</span>
            <Select value={threshold} onValueChange={setThreshold}>
              <SelectTrigger data-testid="backtest-threshold-selector" className="mono w-28 h-8 text-[11px] bg-black/25 border-white/[0.08] rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass rounded-2xl border-white/[0.06]">
                {THRESHOLDS.map(t => (
                  <SelectItem key={t.v} value={t.v} data-testid={`backtest-threshold-${t.v}`} className="mono text-[11px] rounded-lg">{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mono text-[11px] text-[color:var(--text-tertiary)] mt-1">Benchmarks: ASX → {bench.ASX || "^AXJO"} · US → {bench.US || "^GSPC"} · Live yfinance</div>
        </div>
        <div className="flex items-center gap-1.5 p-1 rounded-full bg-black/25 border border-white/[0.04]" data-testid="horizon-select">
          {["r7", "r30", "r90"].map((h) => (
            <button
              key={h}
              onClick={() => setHorizon(h)}
              data-testid={`horizon-${h}`}
              className={`mono text-[11px] px-4 py-1.5 rounded-full font-medium transition-colors ${horizon === h ? "text-[#0f1420]" : "text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"}`}
              style={horizon === h ? { background: "linear-gradient(135deg, var(--violet), var(--cyan))" } : {}}
            >T+{h.slice(1)}</button>
          ))}
        </div>
      </div>

      <div className="glass p-5" style={{ height: 500 }} data-testid="scatter-chart">
        {loading ? (
          <div className="mono text-[color:var(--text-tertiary)] terminal-loader">Fetching yfinance data</div>
        ) : points.length === 0 ? (
          <div className="mono text-[color:var(--text-tertiary)] flex items-center justify-center h-full">No valid data points. Approve items in the Review Queue to surface more signals.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 40 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" dataKey="x" domain={[50, 100]} name="Score" tick={{ fill: "#8892ac", fontFamily: "Geist Mono", fontSize: 11 }} stroke="rgba(255,255,255,0.10)" label={{ value: "CONVICTION SCORE", position: "insideBottom", offset: -20, fill: "#5b6382", fontSize: 10 }} />
              <YAxis type="number" dataKey="y" name="Return %" tick={{ fill: "#8892ac", fontFamily: "Geist Mono", fontSize: 11 }} stroke="rgba(255,255,255,0.10)" label={{ value: `RETURN T+${horizon.slice(1)} %`, angle: -90, position: "insideLeft", fill: "#5b6382", fontSize: 10 }} />
              <ZAxis range={[80, 240]} />
              <ReferenceLine y={0} stroke="#4dd7f3" strokeDasharray="3 3" strokeOpacity={0.6} />
              <Tooltip
                cursor={{ stroke: "#4dd7f3", strokeDasharray: "2 2", strokeOpacity: 0.5 }}
                content={<ScatterTooltip horizon={horizon} />}
              />
              <Scatter data={points} fill="#4dd7f3" shape="circle" />
            </ScatterChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-4 flex items-center gap-5 mono text-[11px] text-[color:var(--text-tertiary)]">
        <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "#5eead4" }} />ASX vs ^AXJO</span>
        <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "#7db3ff" }} />NYSE vs ^GSPC</span>
        <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "#b593ff" }} />NASDAQ vs ^GSPC</span>
        <span className="ml-auto">Hit rate = signal outperformed benchmark by direction (buy&gt;bench, sell&lt;bench).</span>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, grad, testKey }) {
  const key = testKey || label.split(" ")[0].toLowerCase();
  return (
    <div className="glass p-5" data-testid={`kpi-${key}`}>
      <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-secondary)] font-medium mb-3">{label}</div>
      <div className={`mono text-[28px] font-semibold tracking-tight leading-none ${grad ? "grad-text" : ""}`} style={{ color: !grad && color ? color : undefined }}>{value}</div>
    </div>
  );
}

function ScatterTooltip({ horizon, active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const bkey = "b" + horizon.slice(1);
  return (
    <div className="glass p-3 rounded-xl" style={{ fontFamily: "Geist Mono", fontSize: 11 }}>
      <div style={{ color: marketColor(p.market), fontWeight: 700 }}>{p.ticker} · {p.market}</div>
      <div style={{ color: "#8892ac" }}>score {p.score.toFixed(2)}</div>
      <div style={{ color: "#eef1f8" }}>return {p.y?.toFixed(2)}%</div>
      <div style={{ color: "#5b6382" }}>bench {p[bkey]?.toFixed(2)}%</div>
      <div style={{ color: (p.y - (p[bkey] || 0)) >= 0 ? "#5eead4" : "#f38fb2" }}>alpha {(p.y - (p[bkey] || 0)).toFixed(2)}%</div>
      {p.stale && <div style={{ color: "#f5df7c" }}>stale price data</div>}
    </div>
  );
}
